import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { motion } from "framer-motion";
import { SEOHead } from "@/components/SEOHead";

const Privacidad = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Política de Privacidad y Protección de Datos | CreceLocal"
        description="Conocé cómo protegemos y gestionamos tus datos personales y comerciales. Nuestra política de privacidad detalla el uso, seguridad y almacenamiento de tu información."
        canonical="/privacidad"
      />
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-[800px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="prose prose-invert prose-sm sm:prose-base md:prose-lg max-w-none"
          >
            <h1 className="text-4xl md:text-5xl font-medium tracking-tight mb-8 text-foreground">
              Política de Privacidad
            </h1>

            <p className="text-muted-foreground mb-12">
              <strong>CreceLocal LLC</strong>
              <br />
              Última actualización: junio 2025
            </p>

            <div className="space-y-12 text-muted-foreground">
              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  1. Introducción
                </h2>
                <p>
                  CreceLocal LLC ("CreceLocal", "nosotros") opera el sitio web
                  crecelocal.com y la plataforma de automatización comercial.
                  Esta Política de Privacidad describe cómo recopilamos, usamos,
                  almacenamos y protegemos tu información personal.
                </p>
                <p className="mt-4">
                  Al usar nuestros servicios, aceptás las prácticas descritas en
                  este documento.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  2. Información que recopilamos
                </h2>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-3">
                  Información que nos proporcionás directamente
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Nombre y apellido</li>
                  <li>Dirección de correo electrónico</li>
                  <li>Número de WhatsApp / teléfono</li>
                  <li>Nombre y sector de tu negocio</li>
                  <li>
                    Información de pago (procesada por terceros — no almacenamos
                    datos de tarjetas)
                  </li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-3">
                  Información recopilada automáticamente
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Dirección IP</li>
                  <li>Tipo de navegador y dispositivo</li>
                  <li>Páginas visitadas y tiempo en el sitio</li>
                  <li>Cookies y tecnologías similares de seguimiento</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-3">
                  Información de los contactos de tu negocio
                </h3>
                <p>
                  Al usar la plataforma, podés cargar datos de tus propios
                  clientes y prospectos. Vos sos el responsable de esos datos.
                  CreceLocal los procesa en tu nombre como encargado del
                  tratamiento.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  3. Cómo usamos tu información
                </h2>
                <p className="mb-4">Usamos tu información para:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Proveer, operar y mejorar el servicio</li>
                  <li>Procesar pagos y gestionar tu suscripción</li>
                  <li>
                    Enviarte comunicaciones relacionadas con tu cuenta y el
                    servicio
                  </li>
                  <li>Responder consultas y brindar soporte</li>
                  <li>
                    Analizar el uso de la plataforma para mejorar la experiencia
                  </li>
                  <li>Cumplir con obligaciones legales</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  4. Compartir información con terceros
                </h2>
                <p className="mb-4">
                  No vendemos ni alquilamos tu información personal. Podemos
                  compartirla con:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Proveedores de servicio:</strong> procesadores de
                    pago, plataformas de infraestructura y herramientas de
                    análisis que nos ayudan a operar el servicio
                  </li>
                  <li>
                    <strong>Plataformas integradas:</strong> Meta (WhatsApp
                    Business API, Facebook, Instagram), Google, TikTok — sujeto
                    a sus propias políticas de privacidad
                  </li>
                  <li>
                    <strong>Requerimientos legales:</strong> cuando sea exigido
                    por ley, orden judicial o autoridad competente
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  5. Cookies
                </h2>
                <p>
                  Usamos cookies para mejorar la experiencia de navegación,
                  analizar el tráfico y personalizar el contenido. Podés
                  configurar tu navegador para rechazar cookies, aunque esto
                  puede afectar el funcionamiento del sitio.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  6. Retención de datos
                </h2>
                <p>
                  Conservamos tu información mientras tu cuenta esté activa o
                  sea necesaria para proveer el servicio. Luego de la
                  cancelación, podemos retener ciertos datos por hasta 12 meses
                  por razones legales o de seguridad, tras lo cual los
                  eliminamos de forma segura.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  7. Seguridad
                </h2>
                <p>
                  Implementamos medidas técnicas y organizativas razonables para
                  proteger tu información contra acceso no autorizado, pérdida o
                  alteración. Sin embargo, ningún sistema de seguridad es
                  infalible y no podemos garantizar la seguridad absoluta de los
                  datos.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  8. Tus derechos
                </h2>
                <p className="mb-4">Tenés derecho a:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Acceder a la información personal que tenemos sobre vos
                  </li>
                  <li>Solicitar la corrección de datos inexactos</li>
                  <li>
                    Solicitar la eliminación de tus datos, sujeto a obligaciones
                    legales
                  </li>
                  <li>
                    Oponerte al procesamiento de tus datos para ciertos fines
                  </li>
                  <li>Portabilidad de tus datos en formato legible</li>
                </ul>
                <p className="mt-4">
                  Para ejercer cualquiera de estos derechos, escribinos a
                  info@crecelocal.com.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  9. Privacidad de menores
                </h2>
                <p>
                  CreceLocal no está dirigido a menores de 18 años y no
                  recopilamos intencionalmente información de menores. Si
                  detectamos que tenemos datos de un menor, los eliminaremos de
                  inmediato.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  10. Transferencias internacionales de datos
                </h2>
                <p>
                  CreceLocal opera desde Estados Unidos. Si accedés al servicio
                  desde otro país, tus datos pueden ser transferidos y
                  procesados en Estados Unidos, donde las leyes de protección de
                  datos pueden diferir de las de tu país de residencia.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  11. Cambios a esta política
                </h2>
                <p>
                  Podemos actualizar esta política periódicamente. Te
                  notificaremos por email o mediante un aviso en la plataforma
                  ante cambios significativos. El uso continuado del servicio
                  luego de la notificación implica la aceptación de los cambios.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  12. Contacto
                </h2>
                <p>Para consultas sobre esta Política de Privacidad:</p>
                <div className="mt-4">
                  <p>CreceLocal LLC</p>
                  <p>131 Continental Dr, Suite 305</p>
                  <p>Newark, DE 19713</p>
                  <p>Estados Unidos</p>
                  <p>info@crecelocal.com</p>
                  <p>crecelocal.com</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Privacidad;
